import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, FileText, DollarSign, PieChart } from 'lucide-react';
import ClientSidebar from '../Sidebar/ClientSidebar';
import { useToast } from '../../hooks/use-toast';

const ClientStats = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/client`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <ClientSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="text-center py-12">
            <p className="text-[#A6A6A6]">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Suivi</span> et Statistiques
            </h1>
            <p className="text-[#A6A6A6]">Analysez votre activité sur la plateforme</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Total Commandes</p>
                    <p className="text-3xl font-bold text-white">{stats?.total_orders || 0}</p>
                  </div>
                  <FileText className="h-10 w-10 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Dépenses Totales</p>
                    <p className="text-3xl font-bold text-[#C00000]">{stats?.total_spent?.toFixed(2) || 0}€</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-1">Panier Moyen</p>
                    <p className="text-3xl font-bold text-white">
                      {stats?.total_orders > 0 ? (stats.total_spent / stats.total_orders).toFixed(2) : 0}€
                    </p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Services Breakdown */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PieChart className="h-5 w-5 text-[#C00000]" />
                Répartition par Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.services_breakdown && stats.services_breakdown.length > 0 ? (
                <div className="space-y-4">
                  {stats.services_breakdown.map((service, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">{service._id}</span>
                        <div className="text-right">
                          <span className="text-[#C00000] font-bold">{service.percentage.toFixed(1)}%</span>
                          <span className="text-[#A6A6A6] text-sm ml-3">({service.count} commandes)</span>
                        </div>
                      </div>
                      <div className="w-full bg-[#0a0a0a] rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-[#C00000] to-[#C00000]/70 h-3 rounded-full transition-all"
                          style={{ width: `${service.percentage}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-[#A6A6A6] text-sm">Total dépensé</span>
                        <span className="text-white font-semibold">{service.total?.toFixed(2) || 0}€</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A6A6A6] text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Status Breakdown */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardHeader>
              <CardTitle className="text-white">Statut des Commandes</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.status_breakdown && stats.status_breakdown.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {stats.status_breakdown.map((status, index) => (
                    <div key={index} className="bg-[#0a0a0a] p-4 rounded-lg">
                      <p className="text-[#A6A6A6] text-sm mb-1 capitalize">{status._id}</p>
                      <p className="text-2xl font-bold text-white">{status.count}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A6A6A6] text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientStats;